import { create } from 'zustand';
import { persist, type PersistStorage, type StorageValue } from 'zustand/middleware';
import {
  endImpersonationRequest,
  loginRequest,
  logoutRequest,
  registerAuthBridge,
  startImpersonationRequest,
  switchHatRequest,
  type AuthRole,
  type Hat,
  type ImpersonatorInfo,
  type LoginResponse,
} from '../services/api';

export type { Hat };

export interface AuthUser {
  id: string;
  email: string;
  role: AuthRole;
  /** Display name, editable on /profile. */
  name?: string | null;
  /**
   * Admin tier (operators only). Display-only: every admin action is
   * re-checked server-side against the database, so flipping this in
   * the console only changes which buttons render.
   */
  is_admin: boolean;
  lender_id: string | null;
  /** SHA-256 hash of the debtor's CPR. Set only when role === 'debtor'. */
  subject_key: string | null;
  /**
   * Lender review status — `pending_review` / `approved` / `rejected`.
   * Populated only when role === 'lender_human'. Drives whether the
   * user lands on `/entries` or `/pending-approval` after login.
   */
  lender_review_status: string | null;
  /**
   * Operator's free-text rejection reason. Populated only when
   * `lender_review_status === 'rejected'`. Surfaced on the
   * /pending-approval rejected branch.
   */
  lender_rejection_reason: string | null;
  /**
   * The account behind the token (two hats). `role` above is the
   * EFFECTIVE role the token carries; these say what the account is
   * and which hat it wears. Optional because the invite-accept and
   * creditor-signup responses predate them — `/auth/me` fills them in
   * on the next refresh of the cached record.
   */
  account_role?: AuthRole;
  account_is_admin?: boolean;
  hat?: Hat | null;
  /** The operator's home lender; the hat switch shows exactly when set. */
  home_lender_id?: string | null;
}

/**
 * Shape any "auto-sign-in" response (login, invite-accept, password-
 * reset-complete) maps onto. The user object is loose because some
 * endpoints don't include the lender review fields — the store fills
 * them with `null` so the persisted shape stays consistent.
 */
export interface SessionResponse {
  access_token: string;
  /** Present on a "remember me" login only; other flows omit it. */
  refresh_token?: string | null;
  user: {
    id: string;
    email: string;
    name?: string | null;
    role: AuthRole;
    is_admin?: boolean;
    lender_id?: string | null;
    subject_key?: string | null;
    lender_review_status?: string | null;
    lender_rejection_reason?: string | null;
    account_role?: AuthRole;
    account_is_admin?: boolean;
    hat?: Hat | null;
    home_lender_id?: string | null;
  };
}

/** Normalise any sign-in response's user block onto `AuthUser`. */
function toAuthUser(u: SessionResponse['user']): AuthUser {
  return {
    id: u.id,
    email: u.email,
    role: u.role,
    name: u.name ?? null,
    is_admin: u.is_admin ?? false,
    lender_id: u.lender_id ?? null,
    subject_key: u.subject_key ?? null,
    lender_review_status: u.lender_review_status ?? null,
    lender_rejection_reason: u.lender_rejection_reason ?? null,
    account_role: u.account_role ?? u.role,
    account_is_admin: u.account_is_admin ?? u.is_admin ?? false,
    hat: u.hat ?? null,
    home_lender_id: u.home_lender_id ?? null,
  };
}

// =====================================================================
// Login-as sessions ("Log ind som")
// =====================================================================
//
// An admin can log in as another user (see docs/superpowers/specs/
// 2026-09-30-admin-impersonation-design.md). While that lasts the store
// holds the TARGET's session (a token of the target's own kind, no
// refresh token) together with `impersonation`, and the admin's own
// login stays where it was: in localStorage, untouched.

/** A login-as session as the SPA tracks it. */
export interface ImpersonationSession {
  /** `impersonation.id` (24 hex) — also the token's `act.sid`. */
  id: string;
  /**
   * ISO instant the session ends, as the server states it. Fixed at
   * start; never extended. For DISPLAY ("udløber kl. 14:32") only: the
   * tab ends the session at `deadline`, which does not depend on this
   * browser's clock agreeing with the server's.
   */
  expires_at: string;
  /**
   * When this tab ends the session: epoch milliseconds of the browser's
   * own clock, `Date.now() + expires_in_seconds * 1000` taken when the
   * start response arrived. A clock that is minutes off would move a
   * comparison against `expires_at`; it cannot move this one.
   */
  deadline: number;
  /**
   * A debtor or operator target: the server refuses every write in this
   * session (403 `impersonation_forbidden`), and the banner says so.
   */
  view_only: boolean;
  /** The admin behind it. */
  impersonator: ImpersonatorInfo;
  /** Same-origin path the admin pressed the button on; exit goes back. */
  return_to: string;
}

/** Where a session begins and ends when nothing better is known. */
const DEFAULT_RETURN_TO = '/entries';

/**
 * A same-origin absolute path, or the default. `return_to` is persisted
 * and navigated to, so it must never be able to name another origin
 * (`//host`, `/\host`) or a non-path.
 */
function safeReturnTo(path: unknown): string {
  return typeof path === 'string' && /^\/(?![/\\])/.test(path) ? path : DEFAULT_RETURN_TO;
}

/**
 * Where a user lands after signing in — `/min-sag` for a debtor,
 * `/pending-approval` for a lender whose application is not approved,
 * `/entries` for everyone else. The same table LoginPage uses.
 */
export function landingPathFor(
  user: Pick<AuthUser, 'role' | 'lender_review_status'> | null,
): string {
  if (user?.role === 'debtor') return '/min-sag';
  if (
    user?.role === 'lender_human' &&
    (user.lender_review_status === 'pending_review' ||
      user.lender_review_status === 'rejected')
  ) {
    return '/pending-approval';
  }
  return DEFAULT_RETURN_TO;
}

const ZONE_SUFFIX = /(?:Z|[+-]\d{2}(?::?\d{2})?)$/i;

/**
 * Milliseconds since the epoch for a timestamp the API sent, or NaN.
 * The API sends UTC; a string without a zone designator is read as UTC
 * rather than as the browser's local time, which would move a session's
 * end by the viewer's offset.
 */
export function parseServerInstant(iso: string): number {
  return Date.parse(ZONE_SUFFIX.test(iso) ? iso : `${iso}Z`);
}

// ---------------------------------------------------------------------
// Transitions
// ---------------------------------------------------------------------
//
// Entering or leaving a session changes who every cached answer, every
// open page and every pending request belongs to. The store flips the
// identity at once; what the SPA does about it (empty the query cache,
// navigate, tell the user) needs React and the router, so the store
// publishes a transition here for the always-mounted ImpersonationBanner
// to act on. A separate, never-persisted store: nothing about a
// transition belongs in what survives a reload.

export type ImpersonationTransitionKind =
  /** `startImpersonation` succeeded. `to` is the target's landing page. */
  | 'started'
  /** The admin left (Afslut, or sign-out inside the session). `to` is `return_to`. */
  | 'exited'
  /** The session ended on its own — time ran out or the server refused it. */
  | 'ended';

export interface ImpersonationTransition {
  /** Strictly increasing, so a consumer acts on each transition once. */
  seq: number;
  kind: ImpersonationTransitionKind;
  /**
   * Where to go. `/login` when the admin's own session could not be
   * restored (see `signedOut`).
   */
  to: string;
  /**
   * Router state to navigate with. Set when `to` is `/login`:
   * `{from: return_to}`, which LoginPage honours, so signing in again
   * lands the admin back on the page the session was started from.
   */
  state?: { from: string };
  /**
   * An exit that found no admin login to go back to (signed out, or
   * replaced by another account, in another tab meanwhile — or expired
   * with no refresh token to renew it): the tab is signed out. Always
   * false for `started`.
   */
  signedOut: boolean;
}

export const useImpersonationTransition = create<{
  transition: ImpersonationTransition | null;
}>()(() => ({ transition: null }));

let transitionSeq = 0;

function publishTransition(
  kind: ImpersonationTransitionKind,
  to: string,
  signedOut = false,
  state?: { from: string },
): void {
  useImpersonationTransition.setState({
    transition: { seq: ++transitionSeq, kind, to, signedOut, ...(state ? { state } : {}) },
  });
}

/**
 * A request made inside the session was refused by the session policy
 * (403 `impersonation_forbidden`). `seq` increases on each refusal; the
 * banner says "not possible while logged in as the user" in its notice
 * slot. Ephemeral, like the transition. The refused request's own caller
 * still gets the error and shows what it always showed.
 */
export const useImpersonationRefusal = create<{ seq: number }>()(() => ({ seq: 0 }));

/** The consumer is done with transition `seq`. */
export function acknowledgeImpersonationTransition(seq: number): void {
  if (useImpersonationTransition.getState().transition?.seq === seq) {
    useImpersonationTransition.setState({ transition: null });
  }
}

// ---------------------------------------------------------------------
// Where the session lives
// ---------------------------------------------------------------------
//
//  * The admin's own login is in localStorage["lexta-auth"], shared by
//    every tab, and is NEVER written while this tab is in a session —
//    other tabs keep working as the admin (and may even refresh it).
//  * The session is in sessionStorage["lexta-auth-imp"]: this tab only,
//    gone when the tab closes (the server session then simply runs out).
//
// zustand's `persist` writes the whole persisted slice on every change.
// `routedStorage` decides per write which key it belongs to, from the
// slice itself: `impersonation` set → the tab key only; unset → the
// shared key, and the tab key goes. Reads prefer the tab key.

const SHARED_KEY = 'lexta-auth';
const TAB_KEY = 'lexta-auth-imp';

type PersistedAuth = Pick<
  AuthState,
  'token' | 'refreshToken' | 'user' | 'impersonation'
>;

function readKey(area: 'local' | 'session', key: string): string | null {
  try {
    return (area === 'local' ? localStorage : sessionStorage).getItem(key);
  } catch {
    return null;
  }
}

function writeKey(area: 'local' | 'session', key: string, value: string): void {
  try {
    (area === 'local' ? localStorage : sessionStorage).setItem(key, value);
  } catch {
    // Quota or blocked storage. The in-memory session still works; only
    // surviving a reload is lost. Never fall back to the other area: a
    // session token must not land in the shared key.
  }
}

function dropKey(area: 'local' | 'session', key: string): void {
  try {
    (area === 'local' ? localStorage : sessionStorage).removeItem(key);
  } catch {
    // Nothing to do.
  }
}

// ---------------------------------------------------------------------
// The live note
// ---------------------------------------------------------------------
//
// The server keeps ONE login-as session per admin and lets the admin end
// it by naming its id: `POST /auth/impersonation/end` with the admin's
// own token and `{"impersonation_id"}` ends only that session. A stale
// tab's clean-up can therefore never end a newer one, and nothing here
// has to guard against it.
//
// What the SPA still needs is for one tab to know about a session that
// ANOTHER tab of this browser is in. The admin who signs out in tab B
// (which is not in a session) ends the session tab A holds — and B has
// neither A's session token nor its id. So a tab that starts a session
// leaves a note in localStorage: the session's id and end time, nothing
// secret (no token), and takes it back when that session ends. The
// admin's own sign-out reads it (`logout`). A session started in another
// BROWSER cannot be seen from here; the backend ends that one when the
// sign-out carries a refresh token.

const LIVE_KEY = 'lexta-imp-live';

function markLiveSession(id: string, expiresAt: string): void {
  writeKey('local', LIVE_KEY, JSON.stringify({ id, expires_at: expiresAt }));
}

/** Take the note back — but only if it still names `id`: a newer
 *  session (this tab's or another's) has overwritten it, and owns it. */
function unmarkLiveSession(id: string): void {
  const note = readLiveNote();
  if (note && note.id === id) dropKey('local', LIVE_KEY);
}

function readLiveNote(): { id: string; expires_at: string } | null {
  const raw = readKey('local', LIVE_KEY);
  if (!raw) return null;
  try {
    const note: unknown = JSON.parse(raw);
    if (isRecord(note) && isNonEmptyString(note.id) && isNonEmptyString(note.expires_at)) {
      return { id: note.id, expires_at: note.expires_at };
    }
  } catch {
    // Unreadable: no note.
  }
  return null;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isNonEmptyString(value: unknown): value is string {
  return typeof value === 'string' && value.length > 0;
}

/** Parse what `persist` wrote: `{state, version}`; null if it is not that. */
function parseStored(raw: string | null): StorageValue<PersistedAuth> | null {
  if (!raw) return null;
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!isRecord(parsed) || !isRecord(parsed.state)) return null;
    return parsed as unknown as StorageValue<PersistedAuth>;
  } catch {
    return null;
  }
}

/**
 * The tab key holds a usable session: a token, a user, and a session
 * whose id / expiry / admin / return path are all present. Anything
 * less is junk, never hydrated.
 */
function isSessionState(state: Partial<PersistedAuth>): boolean {
  const imp: unknown = state.impersonation;
  const user: unknown = state.user;
  return (
    isNonEmptyString(state.token) &&
    isRecord(user) &&
    isNonEmptyString(user.id) &&
    isNonEmptyString(user.email) &&
    isNonEmptyString(user.role) &&
    isRecord(imp) &&
    isNonEmptyString(imp.id) &&
    isNonEmptyString(imp.expires_at) &&
    isRecord(imp.impersonator) &&
    isNonEmptyString(imp.impersonator.id) &&
    typeof imp.return_to === 'string'
  );
}

/**
 * The session as it comes back from the tab key. Every part is made to
 * fit the type: a blob written before `deadline` / `view_only` existed
 * gets its deadline from `expires_at`, and one whose end cannot be told
 * at all gets NaN — which `checkImpersonationDeadline` reads as "over",
 * so a session whose end is unknown never runs.
 */
function hydrateSession(raw: Record<string, unknown>): ImpersonationSession {
  const storedDeadline = raw.deadline;
  return {
    id: raw.id as string,
    expires_at: raw.expires_at as string,
    deadline:
      typeof storedDeadline === 'number' && Number.isFinite(storedDeadline)
        ? storedDeadline
        : parseServerInstant(raw.expires_at as string),
    view_only: raw.view_only === true,
    impersonator: raw.impersonator as ImpersonatorInfo,
    return_to: safeReturnTo(raw.return_to),
  };
}

const routedStorage: PersistStorage<PersistedAuth> = {
  getItem: (name) => {
    const tab = parseStored(readKey('session', TAB_KEY));
    if (tab) {
      if (isSessionState(tab.state)) {
        return {
          ...tab,
          state: {
            ...tab.state,
            // A session never has a refresh token; hold the line even
            // if the stored copy somehow does.
            refreshToken: null,
            impersonation: hydrateSession(tab.state.impersonation as unknown as Record<string, unknown>),
          },
        };
      }
      dropKey('session', TAB_KEY);
    }

    const shared = parseStored(readKey('local', name));
    // A session token must never sit in the shared key: whoever finds it
    // there (an older build, a hand edit) is signed out rather than
    // handed an impersonation with no tab behind it.
    if (!shared || shared.state.impersonation) return null;
    return shared;
  },

  setItem: (name, value) => {
    if (value.state.impersonation) {
      writeKey('session', TAB_KEY, JSON.stringify(value));
      return;
    }
    const leavingSession = readKey('session', TAB_KEY) !== null;
    dropKey('session', TAB_KEY);
    // Leaving a session with nobody to return to (the admin signed out
    // in another tab, or another account signed in there): the shared
    // key is that other tab's business. Writing this tab's signed-out
    // state over it would sign them out too.
    if (leavingSession && value.state.token === null) return;
    writeKey('local', name, JSON.stringify(value));
  },

  removeItem: (name) => {
    dropKey('session', TAB_KEY);
    dropKey('local', name);
  },
};

interface AdminLogin {
  token: string;
  refreshToken: string | null;
  user: AuthUser;
}

/**
 * The admin's own login, re-read from the shared key at the moment the
 * session ends (so a refresh another tab did meanwhile is picked up).
 * Null when it is gone, is not the admin who started this session, or
 * is itself a session — none of which may be "restored".
 */
function readSharedAdminSession(adminId: string): AdminLogin | null {
  const state = parseStored(readKey('local', SHARED_KEY))?.state;
  if (!state || state.impersonation) return null;
  const user: unknown = state.user;
  if (!isNonEmptyString(state.token) || !isRecord(user) || user.id !== adminId) {
    return null;
  }
  return {
    token: state.token,
    refreshToken: isNonEmptyString(state.refreshToken) ? state.refreshToken : null,
    user: user as unknown as AuthUser,
  };
}

/**
 * `exp` of a JWT in epoch milliseconds, or null when the token is not one
 * this can read. Base64url JSON, no library and NO signature check: the
 * answer only decides whether an access token is worth putting back in
 * the store, never whether it is trusted (the server does that).
 */
export function jwtExpiryMs(token: string): number | null {
  const parts = token.split('.');
  if (parts.length !== 3 || !parts[1]) return null;
  try {
    const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const binary = atob(base64.padEnd(Math.ceil(base64.length / 4) * 4, '='));
    const claims: unknown = JSON.parse(
      new TextDecoder().decode(Uint8Array.from(binary, (c) => c.charCodeAt(0))),
    );
    if (isRecord(claims) && typeof claims.exp === 'number' && Number.isFinite(claims.exp)) {
      return claims.exp * 1000;
    }
  } catch {
    // Not base64, not JSON: unreadable.
  }
  return null;
}

/** An access token with less life than this is treated as already dead:
 *  the first request made with it would race its own expiry. */
const MIN_ACCESS_TOKEN_LIFE_MS = 10_000;

/** False for an access token that has expired or will within 10 seconds.
 *  A token that cannot be read is given the benefit of the doubt (true):
 *  the first request will find out. */
function isAccessTokenLive(token: string, now: number = Date.now()): boolean {
  const expiresAt = jwtExpiryMs(token);
  return expiresAt === null || expiresAt > now + MIN_ACCESS_TOKEN_LIFE_MS;
}

/**
 * Whether the admin's stored login may be put back as a LIVE session when
 * a login-as ends: its access token is alive, or it has a refresh token,
 * which the normal refresh path (the api client's 401 handling) turns
 * into a new access token on the first request. A login with neither is
 * dead — restoring it would only send the admin's first request to a 401
 * and a hard sign-out redirect that forgets where they were.
 */
function isRestorable(login: AdminLogin): boolean {
  return login.refreshToken !== null || isAccessTokenLive(login.token);
}

// =====================================================================
// The store
// =====================================================================

interface AuthState {
  token: string | null;
  /**
   * Opaque `lxr_` refresh token behind a "remember me" login, or null.
   * The api client's response interceptor reads it on a 401 to mint a
   * new access token silently; `logout()` revokes it server-side.
   * Always null inside a login-as session.
   */
  refreshToken: string | null;
  user: AuthUser | null;
  /**
   * The login-as session this tab is in, or null. While set, `token` and
   * `user` are the TARGET's; the admin's own login is in localStorage.
   */
  impersonation: ImpersonationSession | null;
  /**
   * Counts every change of WHO the store is (a login-as session starting
   * or ending, a sign-in replacing one). Never persisted, and it never
   * goes back to a value it had: App keys the page subtree on it
   * (IdentityGate). The session id itself cannot do that job — it is
   * `null` both before a session and after it, so a page from before
   * that is still animating out when the session ends would look like it
   * belongs to the present again.
   */
  identityEpoch: number;
  loading: boolean;
  /** `remember` = the "remember me" checkbox; off keeps today's 60-minute session. */
  login: (email: string, password: string, remember?: boolean) => Promise<void>;
  /**
   * Sign out. Inside a login-as session it ends the session instead —
   * the admin's own login behind it is not this tab's to sign out.
   * Outside one, an admin's sign-out first ends the login-as session
   * this browser knows about (the live note: one another tab is in),
   * best effort, with the admin's token and that session's id, while the
   * token is still in hand. Components go through `useSignOut()`.
   */
  logout: () => void;
  setUser: (user: AuthUser | null) => void;
  setToken: (token: string | null) => void;
  /**
   * Drop a freshly-issued session into the store. Used by flows that
   * mint a JWT outside the login endpoint — invite accept and
   * password-reset complete. Lender-review fields default to `null`
   * when the response doesn't carry them.
   */
  setSession: (response: SessionResponse) => void;
  /**
   * Two hats: an operator account with a home lender can put on the
   * lender hat (a real `lender_human` token for that lender) and back.
   * `switchHat` swaps the access token and the user block; the rest of
   * the SPA keys off the effective `role`, so the lender experience
   * follows automatically. The remembered session is untouched. Allowed
   * inside a login-as session: the server carries the session across.
   */
  canSwitchHat: () => boolean;
  switchHat: (hat: Hat) => Promise<void>;
  // Role-derived selectors. Cheap field reads — use these instead of
  // sprinkling `user.role === 'operator'` through components.
  isOperator: () => boolean;
  /** Operator carrying the admin tier. Display gating only. */
  isAdmin: () => boolean;
  isLenderHuman: () => boolean;
  isDebtor: () => boolean;
  /** This tab is in a login-as session. */
  isImpersonating: () => boolean;
  /**
   * Ask the server for a login-as session as `userId` and, when it says
   * yes, become that user in this tab. `returnTo` is the page the admin
   * is on; leaving the session comes back to it. Rejects with the
   * request's own error when refused (see `startImpersonationRequest`
   * for the codes), and with a plain Error if the answer cannot be
   * managed — in which case the server session is closed again.
   * Publishes a `started` transition.
   */
  startImpersonation: (
    userId: string,
    reason: string,
    returnTo: string,
  ) => Promise<void>;
  /**
   * Leave the session: back to the admin's own login, at once and
   * whatever the network does, then tell the server. `expired: true`
   * says it ended on its own (time ran out, a request was refused
   * because the server had already ended it, the admin signed out in
   * another tab) rather than by the admin's choice; it only changes what
   * the SPA says about it.
   * Resolves when the server has been told (or the attempt has failed —
   * it never rejects). No-op outside a session. Publishes an
   * `exited` / `ended` transition. With no usable admin login left to go
   * back to — gone, another account's, or expired with no refresh token
   * — the tab is signed out and the transition points at /login, with
   * `state.from` = where the session was started.
   */
  endImpersonation: (opts?: { expired?: boolean }) => Promise<void>;
}

/**
 * Tell the server a session is over, best effort, naming it by id either
 * way. First with the session's own token (the normal case: it is still
 * good). If that is refused — the token had already died, which is
 * exactly when the SPA ends a session on its own — once more with the
 * ADMIN's token, which the server accepts only together with the id of
 * the session to end: it writes the `ended` row for a session that ran
 * out, and can never touch a newer one, so no guard is needed here.
 *
 * `adminToken` is the admin login this call restored (null when none
 * could be, or for a call that has no admin to speak for). It is taken
 * at that moment and handed in, never read back from the store: by the
 * time the first call has failed the store may hold a NEW session's
 * token, which must never be used to end anything.
 */
async function endOnServer(
  sessionToken: string | null,
  sessionId: string,
  adminToken: string | null,
): Promise<void> {
  if (sessionToken) {
    try {
      await endImpersonationRequest(sessionId, sessionToken);
      return;
    } catch {
      // Fall through to the admin's own call.
    }
  }
  if (adminToken) {
    try {
      await endImpersonationRequest(sessionId, adminToken);
    } catch {
      // Best effort: a session that cannot be reached simply runs out,
      // and the next start (or the admin's sign-out) writes its `ended`.
    }
  }
}

/**
 * Ends the session this tab is in if its deadline has passed, and says
 * how long it has left. Called by the banner once a second and whenever
 * the tab wakes up (`visibilitychange` / `focus` / `pageshow`: timers in
 * a hidden tab or a sleeping laptop run late, this does not depend on
 * them). `null`: not in a session. `0`: it was over, and ending it has
 * begun. A deadline that is not a number counts as over — a session
 * whose end cannot be told never runs.
 */
export function checkImpersonationDeadline(now: number = Date.now()): number | null {
  const { impersonation, endImpersonation } = useAuthStore.getState();
  if (!impersonation) return null;
  const left = impersonation.deadline - now;
  if (!(left > 0)) {
    void endImpersonation({ expired: true });
    return 0;
  }
  return left;
}

/**
 * Lexta operator auth store.
 *
 * - Persists `token` + `refreshToken` + `user` (+ `impersonation`) under
 *   the `lexta-auth` key so a hard refresh restores the session. A
 *   login-as session is persisted to this tab's sessionStorage instead
 *   and leaves the shared localStorage login alone (see `routedStorage`).
 * - `refreshToken` is set only by a "remember me" login. While it is
 *   present the api client swaps an expired access token for a new one
 *   without the user noticing; once the backend refuses it (30 days
 *   from login, or after a logout / password change elsewhere) the
 *   client falls back to the plain sign-out below.
 * - `loading` is intentionally ephemeral (not persisted) — it only
 *   reflects the in-flight login call.
 * - The api client (`src/services/api.ts`) reads `token` and
 *   `refreshToken` from the store through the auth bridge, writes a
 *   refreshed access token back through it, and clears state via
 *   `logout()` on a final 401 response. Inside a login-as session it
 *   never refreshes; a 401 there ends the session (`endImpersonation`).
 */
export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => {
      /** Become whoever this response signs in — `login`, invite accept, password reset. */
      const applySession = (response: SessionResponse) => {
        // A login-as session cannot carry over to whoever signs in now:
        // no admin stands behind the new session to return to. Close it
        // server-side with its own token.
        const leaving = get().impersonation;
        const leavingToken = leaving ? get().token : null;
        set({
          token: response.access_token,
          refreshToken: response.refresh_token ?? null,
          user: toAuthUser(response.user),
          impersonation: null,
          identityEpoch: get().identityEpoch + (leaving ? 1 : 0),
          loading: false,
        });
        if (leaving) {
          unmarkLiveSession(leaving.id);
          if (leavingToken) {
            endImpersonationRequest(leaving.id, leavingToken).catch(() => {});
          }
        }
      };

      return {
        token: null,
        refreshToken: null,
        user: null,
        impersonation: null,
        identityEpoch: 0,
        loading: false,

        login: async (email, password, remember = false) => {
          set({ loading: true });
          try {
            applySession(await loginRequest(email, password, remember));
          } catch (err) {
            set({ loading: false });
            throw err;
          }
        },

        logout: () => {
          if (get().impersonation) {
            void get().endImpersonation();
            return;
          }
          // The admin's own sign-out, in a tab that is not in a session,
          // ends the login-as session this browser knows about — one
          // that ANOTHER tab is in (the live note names it) — while the
          // admin's token is still in hand: it is that token, plus the
          // session's id, which the server needs. Best effort; the
          // backend also ends it when the sign-out below carries a
          // refresh token. Not for anyone who is not an admin: there is
          // nothing of theirs to end.
          const adminToken = get().token;
          const note = adminToken && get().isAdmin() ? readLiveNote() : null;
          if (adminToken && note) {
            // The note is used up only once the server has answered: a
            // call that could not be made (offline, a dead token) leaves
            // it for the next sign-out to try again.
            endImpersonationRequest(note.id, adminToken).then(
              () => unmarkLiveSession(note.id),
              () => {},
            );
          }
          // Clear locally first so the UI reacts at once, then end the
          // remembered session server-side. Best-effort: if the call
          // fails the row expires on its own, and nothing on this device
          // can use it any more.
          const remembered = get().refreshToken;
          set({ token: null, refreshToken: null, user: null, loading: false });
          if (remembered) {
            logoutRequest(remembered).catch(() => {});
          }
        },

        setUser: (user) => set({ user }),
        setToken: (token) => set({ token }),
        setSession: (response) => applySession(response),

        canSwitchHat: () => {
          const u = get().user;
          return u?.account_role === 'operator' && Boolean(u?.home_lender_id);
        },
        switchHat: async (hat) => {
          const response = await switchHatRequest(hat);
          // Token + user only: `refresh_token` is null on this response
          // by contract and the remembered session must survive the swap.
          // Inside a login-as session the response carries the same
          // session; the store's own `impersonation` stays as it is.
          set({ token: response.access_token, user: toAuthUser(response.user) });
        },

        isOperator: () => get().user?.role === 'operator',
        isAdmin: () =>
          get().user?.role === 'operator' && get().user?.is_admin === true,
        isLenderHuman: () => get().user?.role === 'lender_human',
        isDebtor: () => get().user?.role === 'debtor',
        isImpersonating: () => get().impersonation !== null,

        startImpersonation: async (userId, reason, returnTo) => {
          if (get().impersonation) {
            throw new Error('A login-as session is already active in this tab.');
          }
          const adminId = get().user?.id ?? null;

          const response = await startImpersonationRequest(userId, reason);

          const info = response.user.impersonation ?? null;
          const impersonator = response.user.impersonator ?? null;
          const seconds: unknown = response.expires_in_seconds;
          const now = get();
          if (
            !info ||
            !isNonEmptyString(info.id) ||
            !isNonEmptyString(info.expires_at) ||
            !impersonator ||
            !isNonEmptyString(impersonator.id) ||
            // The deadline is computed from this length. Without a usable
            // one there is no way to tell when the session ends.
            typeof seconds !== 'number' ||
            !Number.isFinite(seconds) ||
            seconds <= 0 ||
            // The admin signed out, switched account or started another
            // session while the request was in flight: there is nothing
            // to come back to, so do not become anyone.
            now.impersonation ||
            !now.token ||
            now.user?.id !== adminId
          ) {
            // The server has a session the SPA must not (or cannot)
            // manage. Close it with its own token before refusing.
            endImpersonationRequest(info?.id ?? null, response.access_token).catch(() => {});
            throw new Error('Login-as response could not be used.');
          }

          // Taken now, from the length the server stated: a browser
          // clock that disagrees with the server's moves `expires_at`
          // comparisons, never this.
          const deadline = Date.now() + seconds * 1000;
          const user = toAuthUser(response.user);
          set({
            token: response.access_token,
            // Never a refresh token, whatever the response carries.
            refreshToken: null,
            user,
            impersonation: {
              id: info.id,
              expires_at: info.expires_at,
              deadline,
              view_only: info.view_only === true,
              impersonator,
              return_to: safeReturnTo(returnTo),
            },
            identityEpoch: now.identityEpoch + 1,
            loading: false,
          });
          markLiveSession(info.id, info.expires_at);
          publishTransition('started', landingPathFor(user));
        },

        endImpersonation: async (opts) => {
          const imp = get().impersonation;
          if (!imp) return;
          const sessionToken = get().token;
          const returnTo = safeReturnTo(imp.return_to);

          // Locally and at once — this must not wait for, or depend on,
          // the network. `impersonation` flips to null before anything
          // is awaited, so N simultaneous 401s, the timer and a button
          // press all find nothing left to end after the first.
          //
          // Back to the admin's own login if it is still there AND usable
          // (see `isRestorable`: an expired access token with no refresh
          // token is not). Otherwise the tab is signed out — without
          // writing the shared key, which is another tab's business now —
          // and goes to /login carrying `from` = where the session began.
          const found = readSharedAdminSession(imp.impersonator.id);
          const admin = found && isRestorable(found) ? found : null;
          const identityEpoch = get().identityEpoch + 1;
          if (admin) {
            set({
              token: admin.token,
              refreshToken: admin.refreshToken,
              user: admin.user,
              impersonation: null,
              identityEpoch,
              loading: false,
            });
          } else {
            set({
              token: null,
              refreshToken: null,
              user: null,
              impersonation: null,
              identityEpoch,
              loading: false,
            });
          }
          publishTransition(
            opts?.expired ? 'ended' : 'exited',
            admin ? returnTo : '/login',
            !admin,
            admin ? undefined : { from: returnTo },
          );

          unmarkLiveSession(imp.id);
          await endOnServer(sessionToken, imp.id, admin?.token ?? null);
        },
      };
    },
    {
      name: SHARED_KEY,
      storage: routedStorage,
      // `loading` is request-scoped, never restored across reloads.
      partialize: (state): PersistedAuth => ({
        token: state.token,
        refreshToken: state.refreshToken,
        user: state.user,
        impersonation: state.impersonation,
      }),
    },
  ),
);

// Wire the api client to read tokens from / clear state on this store.
// Done at module-eval so the interceptors are armed before any request
// fires. This module is imported by main.tsx via App.tsx -> components.
registerAuthBridge({
  getToken: () => useAuthStore.getState().token,
  // `?? null` covers a store persisted before the field existed. A
  // login-as session is never refreshed, so it has none to offer.
  getRefreshToken: () => {
    const s = useAuthStore.getState();
    return s.impersonation ? null : (s.refreshToken ?? null);
  },
  getHat: () => useAuthStore.getState().user?.hat ?? null,
  getImpersonationId: () => useAuthStore.getState().impersonation?.id ?? null,
  // Token + user block; never the refresh token (null on this response).
  // A refresh that lands after a login-as session has begun is the
  // admin's, not the session's: it must not overwrite the session.
  onTokenRefreshed: (response: LoginResponse) => {
    if (useAuthStore.getState().impersonation) return;
    useAuthStore.setState({
      token: response.access_token,
      user: toAuthUser(response.user),
    });
  },
  onUnauthorized: () => useAuthStore.getState().logout(),
  onImpersonationEnded: () => {
    void useAuthStore.getState().endImpersonation({ expired: true });
  },
  onImpersonationForbidden: () => {
    useImpersonationRefusal.setState((s) => ({ seq: s.seq + 1 }));
  },
});

// The admin signed out — or another account signed in — in ANOTHER tab of
// this browser while this one is in a login-as session: the login this
// session was started from is gone, and with it the point of the session.
// End it here (locally at once, and on the server with the session's own
// token) and say so; the exit finds no admin to return to and lands on
// /login. `storage` events reach only OTHER tabs — never the one that
// made the write — so this tab's own writes (an exit restoring the admin,
// a hat switch) cannot fire it, and what it does in response writes
// nothing to the shared key, so two tabs cannot set each other off.
// Anything that leaves the admin's login in place, refreshed or not, is
// not a reason: `readSharedAdminSession` asks the key itself, not the
// event, what it holds now.
function endSessionIfAdminLoginChanged(event: StorageEvent): void {
  // `key === null`: the whole area was cleared.
  if (event.key !== null && event.key !== SHARED_KEY) return;
  const imp = useAuthStore.getState().impersonation;
  if (!imp) return;
  try {
    if (event.storageArea && event.storageArea !== localStorage) return;
  } catch {
    return;
  }
  if (readSharedAdminSession(imp.impersonator.id) !== null) return;
  void useAuthStore.getState().endImpersonation({ expired: true });
}

if (typeof window !== 'undefined' && typeof window.addEventListener === 'function') {
  window.addEventListener('storage', endSessionIfAdminLoginChanged);
}
